// src/services/systemText.js
//
// 🟢 GROUP SYSTEM MESSAGES — the web's copy of the phone's systemMessageText()
//    (allo-app/services/notifStrings.ts:189). "Abdullah left the group", "Sara has been
//    added by You", "Only admins can send messages now" — every one of these was landing
//    in the browser as an ordinary chat bubble with the sender's raw English `text` in
//    it, because nothing here knew what a systemType was.
//
// 🟥 TWO DESCRIPTION LAYERS, AND THE NEW ONE WINS. Every system row carries a legacy
//    `systemType` + a pre-baked English `text`, and — since the app's i18n pass — a
//    structured `sysKind` + `sysParams`. The structured pair is authoritative; the legacy
//    fields are the fallback for rows written before it existed, which is what
//    kindFromLegacy() reconstructs. Reading `text` first would freeze every sentence in
//    whatever the writer's language happened to be.

import { isGhostRow } from './ghostRows';

/** systemType values that are NOT rows: side-effect records and control messages. */
const NOT_A_ROW = new Set(['text', 'null', 'undefined', 'reaction', 'vote', 'delete_command']);

/** Is this message a centred system row rather than a chat bubble? */
export function isSystemMessage(m) {
  if (!m) return false;
  /* 🟥 `m.type === 'system'` ALONE USED TO BE ENOUGH, and it is not: a system row
     carries its sentence in `systemType`, so one that arrives without a kind has
     no sentence to centre — it drew an empty line. The phone had the same hole
     and the same symptom (an empty bubble, and "no message yet" in the list
     above it); isGhostRow is the one answer both clients now ask. */
  if (isGhostRow(m)) return false;
  const id = String(m.systemType ?? m.sysKind ?? '');
  if (id === 'delete_command' || m.systemType === 'delete_command') return false;
  // call_history is a system message but renders as its own call row — the caller
  // checks isCallMessage() first, so it never reaches here.
  return m.type === 'system' || (!!id && !NOT_A_ROW.has(id));
}

/** Rows that exist only as machinery and must never be drawn at all.
 *
 *  🟢 ONE PREDICATE, SHARED WITH THE PHONE (services/ghostRows.js is generated —
 *  edit services/ghostRows.ts in allo-app). This used to name the two action
 *  documents itself, which is a fourth copy of a rule that already existed in
 *  three places and disagreed with all of them. */
export function isHiddenSystem(m) {
  return isGhostRow(m);
}

// The sentence table, word-for-word with the phone's `T` map (notifStrings.ts:23).
const S = {
  group_created: '{name} created the group "{groupName}"',
  added_to_group: '{name} added you to "{groupName}"',
  name_change: '{name} changed the group name to "{groupName}"',
  desc_changed: '{name} changed the group description',
  photo_updated: '{name} updated the group photo',
  photo_removed: '{name} removed the group photo',
  joined_invite: '{name} joined via invite',
  user_joined: '{name} joined the group',
  user_left: '{name} left the group',
  user_removed: '{target} was removed by {sender}',
  member_added: '{member} has been added by {adder}',
  member_request: '{adder} invited {member}. Waiting for admin approval.',
  admin_promoted: '{sender} promoted {target} to admin',
  admin_demoted: '{sender} dismissed {target} as admin',
  admin_auto: '{target} is now an admin',
  perm_msgs_all: '{name} allowed all members to send messages',
  perm_msgs_admin: '{name} allowed only admins to send messages',
  perm_calls_all: '{name} allowed all members to start calls',
  perm_calls_admin: '{name} allowed only admins to start calls',
  perm_edit_all: '{name} allowed members to edit group info',
  perm_edit_admin: '{name} allowed only admins to edit group info',
  perm_approve_on: '{name} turned on admin approval to join this group',
  perm_approve_off: '{name} turned off admin approval to join this group',
  perm_add_all: '{name} allowed all members to add others',
  perm_add_admin: '{name} allowed only admins to add members',
  disappearing_on: '{name} turned on disappearing messages ({time})',
  disappearing_off: '{name} turned off disappearing messages',
  screenshot: '{name} took a screenshot',
  loc_stopped: 'Live location sharing stopped',
};

/**
 * Rebuild a sysKind for a row written before sysKind existed.
 * Mirrors notifStrings.ts:198 exactly, including its quirk that a legacy `user_joined`
 * resolves to "joined via invite" — the phone renders it that way too, and a web client
 * that said something different about the same document would be the odd one out.
 */
function kindFromLegacy(m) {
  const name = m.senderName || m.name || '';
  switch (m.systemType) {
    case 'screenshot': return ['screenshot', { name }];
    case 'user_left': return ['user_left', { name }];
    case 'user_joined': return ['joined_invite', { name }];
    case 'admin_promoted': return ['admin_promoted', { sender: name, target: m.targetName || '' }];
    case 'admin_demoted': return ['admin_demoted', { sender: name, target: m.targetName || '' }];
    case 'user_removed': return m.targetName
      // 🟥 senderName holds the VICTIM's name on this type (the writer says so), so the
      //    pair only comes out right from sysParams — never read senderName here.
      ? ['user_removed', { sender: '', target: m.targetName }]
      : [null, null];
    case 'description_changed': return ['desc_changed', { name }];
    case 'group_image_changed': return ['photo_updated', { name }];
    case 'location_stopped': return ['loc_stopped', {}];
    case 'group_name_change': return ['name_change', { name, groupName: m.groupName || '' }];
    case 'member_added': return ['member_added', { member: m.targetName || '', adder: name }];
    default: return [null, null];
  }
}

const BAD_KIND = new Set(['', 'text', 'null', 'undefined', 'reaction', 'vote', 'call_history']);

/**
 * The sentence for a system row, from the point of view of `meId`.
 * Returns '' when there is nothing sensible to say (the caller falls back to m.text).
 */
export function systemText(m, meId) {
  if (!m) return '';
  let kind = String(m.sysKind || '');
  let params = { ...(m.sysParams || {}) };

  if (BAD_KIND.has(kind)) {
    const [k, p] = kindFromLegacy(m);
    if (!k) return String(m.text || '');
    kind = k;
    params = { ...p, ...params };
  }

  // 🟥 "YOU", BUT ONLY FOR THE ACTOR. name/sender/adder are the one DOING it and become
  //    "You"; target/member are the one it was done TO and never do — "You was removed
  //    by Sara" is the sentence that rule exists to prevent.
  if (String(m.senderId || '') === String(meId || '')) {
    if (params.name) params.name = 'You';
    if (params.sender) params.sender = 'You';
    if (params.adder) params.adder = 'You';
  }

  const tpl = S[kind];
  if (!tpl) return String(m.text || '');
  const out = tpl.replace(/\{(\w+)\}/g, (_, k) => (params[k] != null && params[k] !== '' ? String(params[k]) : ''));
  // a template that lost all its names reads as " left the group" — say nothing instead
  return out.trim().replace(/\s{2,}/g, ' ') || String(m.text || '');
}

/** Which glyph the row wears. Keys are systemType, falling back to sysKind. */
export function systemIcon(m) {
  const id = String(m?.systemType || m?.sysKind || '');
  switch (id) {
    case 'group_created': return 'group';
    case 'group_name_change': case 'name_change':
    case 'description_changed': case 'desc_changed': return 'pencil';
    case 'group_image_changed': case 'photo_updated': case 'photo_removed': return 'image';
    case 'user_joined': case 'joined_invite': case 'member_added': return 'person-add';
    case 'user_left': return 'exit';
    case 'user_removed': return 'person-remove';
    case 'member_request': return 'mail';
    case 'admin_promoted': return 'shield-check';
    case 'admin_demoted': return 'shield';
    case 'permission_change': return 'gear';
    case 'disappearing_changed': return 'timer';
    case 'screenshot': return 'camera';
    case 'location_stopped': return 'pin';
    default: return 'info';
  }
}

/* ── group permissions ────────────────────────────────────────────────────── */

/**
 * 🟥 ABSENT MEANS ALLOWED. The phone reads every permission as `!== false`, so a group
 *    created before a flag existed stays permissive. Copying that exactly matters: read
 *    it as `=== true` instead and every older group would silently lock its own members
 *    out of the composer on the web only.
 */
export const isAdmin = (chat, uid) => Array.isArray(chat?.admins) && chat.admins.includes(uid);

export function canSendMessages(chat, uid) {
  if (!chat?.isGroup) return true;
  if (isAdmin(chat, uid)) return true;
  return chat?.permissions?.sendMessages !== false;
}

export function canMakeCalls(chat, uid) {
  if (!chat?.isGroup) return true;
  if (isAdmin(chat, uid)) return true;
  return chat?.permissions?.makeCalls !== false;
}

/** The line under the group name, in the phone's own priority order. */
export function groupSubtitle(chat, uid) {
  if (!chat?.isGroup) return '';
  if (chat?.permissions?.sendMessages === false) return 'Only admins can send messages';
  const left = new Set(chat.leftParticipants || []);
  const active = (chat.participants || []).filter((u) => !left.has(u));
  if (!active.length) return '0 members';
  const nameFor = (u) => (u === uid ? 'You' : (chat.participantNames?.[u] || 'Member'));
  if (active.length === 1) return nameFor(active[0]);
  // the phone puts you first when you're in the list, so the line reads "You & 4 others"
  const head = active.includes(uid) ? uid : active[0];
  return `${nameFor(head)} & ${active.length - 1} others`;
}
